import { FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import NotFound from '../NotFound';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import { dataProjectPath, findDataProject, type DataEntryField } from '../../data/projects';
import { usePageTitle } from '../../hooks/usePageTitle';

function Field({ field, value, onChange }: {
  field: DataEntryField;
  value: string;
  onChange: (value: string) => void;
}) {
  if (field.type === 'select') {
    return (
      <Select
        label={field.label}
        name={field.name}
        value={value}
        options={field.options}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (field.type === 'textarea') {
    return (
      <div className="w-full">
        <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-2">
          {field.label}
        </label>
        <textarea
          id={field.name}
          name={field.name}
          rows={4}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:border-primary focus:ring-2 focus:ring-primary focus:ring-offset-0"
        />
      </div>
    );
  }

  return (
    <Input
      label={field.label}
      name={field.name}
      type={field.type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export default function DataEntry() {
  const { projectSlug } = useParams();
  const project = findDataProject(projectSlug);
  const fields = project?.hasEntry ? project.entryFields ?? [] : [];
  usePageTitle(project?.hasEntry ? `Enter data: ${project.title}` : null);
  const [values, setValues] = useState<Record<string, string>>({});

  if (!project?.hasEntry) {
    return <NotFound />;
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to={dataProjectPath(project.slug)} className="text-sm font-medium text-primary hover:text-primary-dark">
        {project.title}
      </Link>
      <h1 className="mt-2 text-3xl font-bold text-gray-900">Enter data</h1>
      <p className="mt-2 text-gray-600">
        These fields are placeholders. Saving will be available when this project is connected to ORBCoN Tools.
      </p>

      <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
        {fields.map((field) => (
          <div key={field.name} className={field.type === 'textarea' ? 'sm:col-span-2' : undefined}>
            <Field
              field={field}
              value={values[field.name] ?? ''}
              onChange={(next) => setValues((current) => ({ ...current, [field.name]: next }))}
            />
          </div>
        ))}
        <div className="sm:col-span-2">
          <Button type="submit" disabled>
            Save
          </Button>
          <p className="mt-3 text-sm text-gray-600">
            Save stays off until ORBCoN Tools can write this entry to REDCap.
          </p>
        </div>
      </form>
    </div>
  );
}
